/**
 * Helper para salvar/compartilhar PDF de forma robusta em:
 *  - **iOS Capacitor (WKWebView)**: escreve via Filesystem plugin em Cache dir
 *    e abre o **Share Sheet nativo** (opções: Salvar em Arquivos, Imprimir,
 *    enviar por WhatsApp/E-mail, etc.). Sem isso, `<a download>` e blob URLs
 *    são silenciosamente IGNORADOS no WKWebView do iOS.
 *  - **Android Capacitor**: mesma coisa — Filesystem + Share = "abrir com"
 *    aplicativo padrão de PDF.
 *  - **Web (desktop/mobile browser)**: cai no fallback tradicional com
 *    `<a href={blob-url} download>` que funciona no Chrome/Firefox/Edge e
 *    abre inline no Safari (dando ao usuário controle nativo de "Salvar").
 *
 * # Instalação (feita)
 * `yarn add @capacitor/filesystem @capacitor/share`
 *
 * Em app nativo, o Giovanni precisa dar `npx cap sync` e recompilar.
 *
 * # Referências
 * - https://capacitorjs.com/docs/apis/filesystem
 * - https://capacitorjs.com/docs/apis/share
 */

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { FileViewer } from "@capacitor/file-viewer";

/**
 * Verifica se estamos rodando dentro de um app Capacitor nativo (iOS/Android),
 * e não em um browser web. `Capacitor.isNativePlatform()` retorna false
 * quando o código está sendo servido como PWA/web.
 */
export function isNativeApp() {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

/**
 * Converte um Blob para base64 string (sem o prefixo data URL).
 * Necessário porque o plugin Filesystem espera base64 para dados binários.
 */
function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== "string") {
        reject(new Error("FileReader result não é string"));
        return;
      }
      // dataUrl = "data:application/pdf;base64,XXXX...."
      const base64 = dataUrl.split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Abre o PDF em tela cheia no visualizador nativo (iOS QuickLook / Android intent).
 * O visualizador nativo já traz botões de "Compartilhar" e "Salvar" no canto
 * superior direito — perfeito para nossos alunos (menos cliques).
 *
 * Se o FileViewer falhar (algum aparelho antigo sem app padrão de PDF),
 * cai automaticamente no `savePdf` que abre o Share Sheet.
 *
 * @param {Blob} blob - o PDF como Blob (baixado via fetch)
 * @param {string} filename - nome do arquivo (ex.: "certificado_ABC.pdf")
 * @returns {Promise<{platform: 'native-viewer'|'native-share'|'web', canceled?: boolean}>}
 */
export async function openPdf(blob, filename) {
  if (isNativeApp()) {
    // ============================================================
    // NATIVO — Abre no visualizador em tela cheia com botões nativos.
    // ============================================================
    const base64 = await blobToBase64(blob);
    // Escreve em Cache; Cache é apagado pelo SO quando o disco enche.
    const written = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });
    let fileUri = written?.uri;
    if (!fileUri) {
      const uriRes = await Filesystem.getUri({
        path: filename,
        directory: Directory.Cache,
      });
      fileUri = uriRes.uri;
    }

    try {
      // FileViewer.openDocumentFromLocalPath — plugin oficial do Capacitor.
      // No iOS usa QuickLook (visualizador com botões "share" e "save" no topo).
      // No Android usa Intent.ACTION_VIEW (abre no app padrão de PDF).
      await FileViewer.openDocumentFromLocalPath({ path: fileUri });
      return { platform: "native-viewer" };
    } catch (err) {
      // Fallback: se o dispositivo não tiver app de PDF configurado ou o
      // FileViewer falhar por qualquer motivo, mostra o Share Sheet como
      // "plano B" para o aluno ainda conseguir salvar.
      // eslint-disable-next-line no-console
      console.warn("[openPdf] FileViewer falhou, fallback para Share:", err);
      try {
        const result = await Share.share({
          title: "Salvar certificado",
          text: filename,
          url: fileUri,
          dialogTitle: "Salvar certificado",
        });
        return { platform: "native-share", result };
      } catch (shareErr) {
        const msg = String(shareErr?.message || shareErr || "");
        if (/cancel/i.test(msg) || /aborted/i.test(msg)) {
          return { platform: "native-share", canceled: true };
        }
        throw shareErr;
      }
    }
  }

  // ============================================================
  // WEB (browser padrão) — abre em nova aba (PDF inline)
  // ============================================================
  const url = window.URL.createObjectURL(blob);
  window.open(url, "_blank");
  // Revoga em 60s
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  return { platform: "web" };
}

/**
 * Salva/compartilha um PDF blob de forma nativa (Capacitor) ou web.
 *
 * Usado quando o intuito é EXPLICITAMENTE compartilhar/salvar (não visualizar).
 * Para visualização, prefira `openPdf` que abre o PDF em tela cheia.
 *
 * @param {Blob} blob - o PDF como Blob (baixado via fetch).
 * @param {string} filename - nome sugerido do arquivo (ex.: "certificado_ABC.pdf").
 * @param {object} [opts]
 * @param {string} [opts.dialogTitle="Salvar certificado"] - título do share sheet nativo.
 * @param {string} [opts.subject] - assunto para "enviar por e-mail".
 * @returns {Promise<{platform: 'native'|'web', canceled?: boolean}>}
 */
export async function savePdf(blob, filename, opts = {}) {
  const { dialogTitle = "Salvar certificado", subject = filename } = opts;

  if (isNativeApp()) {
    // ============================================================
    // NATIVO (iOS/Android via Capacitor)
    // ============================================================
    const base64 = await blobToBase64(blob);
    // Escreve em Cache (não precisa de permissão) — apagado pelo SO quando espaço acabar.
    const written = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });
    // getUri() garante o caminho absoluto que o Share plugin precisa
    // (o retorno de writeFile já traz `uri`, mas pedir de novo é defensive).
    let fileUri = written?.uri;
    if (!fileUri) {
      const uriRes = await Filesystem.getUri({
        path: filename,
        directory: Directory.Cache,
      });
      fileUri = uriRes.uri;
    }

    try {
      const shareResult = await Share.share({
        title: dialogTitle,
        text: subject,
        url: fileUri,
        dialogTitle,
      });
      return { platform: "native", canceled: false, result: shareResult };
    } catch (err) {
      // Em iOS quando o usuário cancela, o plugin joga uma exceção.
      // Não é erro — só canceled.
      const msg = String(err?.message || err || "");
      if (/cancel/i.test(msg) || /aborted/i.test(msg)) {
        return { platform: "native", canceled: true };
      }
      throw err;
    }
  }

  // ============================================================
  // WEB (browser padrão) — fallback tradicional
  // ============================================================
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revogar em 60s — tempo largo para o browser processar em qualquer plataforma.
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  return { platform: "web" };
}

/**
 * Compartilha um PDF nativamente. No web, tenta Web Share API v2 (com files);
 * se não suportar, cai no `savePdf()` (que abre share/download).
 *
 * @param {Blob} blob
 * @param {string} filename
 * @param {object} [opts]
 * @param {string} [opts.title]
 * @param {string} [opts.text]
 */
export async function sharePdf(blob, filename, opts = {}) {
  const { title = "Meu certificado", text = "Certificado CEAP BRASIL" } = opts;

  if (isNativeApp()) {
    // Nativo — mesmo fluxo do savePdf (share sheet nativo).
    return savePdf(blob, filename, { dialogTitle: title, subject: text });
  }

  // Web — tenta Web Share API v2 (Chrome Android, iOS Safari 15+)
  try {
    if (navigator.canShare) {
      const file = new File([blob], filename, { type: "application/pdf" });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ title, text, files: [file] });
        return { platform: "web-share" };
      }
    }
  } catch (err) {
    // AbortError = usuário cancelou. Nada a fazer.
    if (err?.name === "AbortError") return { platform: "web-share", canceled: true };
    // Silencioso — cai para o fallback.
  }

  // Fallback web — salvar mesmo.
  return savePdf(blob, filename, { dialogTitle: title, subject: text });
}

// Re-exporta constantes úteis (para debug)
export { Capacitor, Directory, Encoding };
